import React from 'react';
import classNames from 'classnames';
import { getThemeConfig } from 'Sites/themes';
import { SiteThemeIcon } from 'Sites/components/SiteThemeIcon';
import { SentryApiClient } from '_generated/api';
import styles from './ThemedHeader.module.css';
import { useSiteThemeOptional } from 'Sites/hooks';

export type ThemedHeaderVariant =
  | 'pill-overlap'
  | 'pill-separate'
  | 'minimal-dark'
  | 'minimal-light';

interface ThemedHeaderBaseProps {
  /** Primary title text */
  theme: SentryApiClient.UIThemeType;
}

interface ThemedHeaderOptions {
  /** Primary title text */
  title: string;
  /** Optional subtitle/tagline */
  subtitle?: string;
  /** Visual variant style */
  variant?: ThemedHeaderVariant;
  /** Render smaller version */
  small?: boolean;
  /** Custom icon to render instead of SiteThemeIcon */
  icon?: React.ReactNode;
  /** Additional CSS class */
  className?: string;
  /** Optional prop for custom fallback content when no theme config is available */
  fallbackComponent?: React.ReactNode;
}

export interface ThemedHeaderProps
  extends ThemedHeaderBaseProps,
    ThemedHeaderOptions {}

/**
 * General-purpose themed header component supporting multiple visual variants.
 *
 * Variants:
 * - `pill-overlap`: Icon overlaps pill edge with white border ring
 * - `pill-separate`: Icon sits to the left of pill with gap
 * - `minimal-dark`: Outline icon + white text (for dark backgrounds)
 * - `minimal-light`: Outline icon + theme-colored text (for light backgrounds)
 *
 * For Default theme, renders without theme styling.
 */
export const ThemedHeader: React.FC<ThemedHeaderProps> = ({
  theme,
  title,
  subtitle,
  variant = 'pill-overlap',
  small,
  icon,
  className,
  fallbackComponent,
}) => {
  const config = getThemeConfig(theme);
  const isPill = variant === 'pill-overlap' || variant === 'pill-separate';
  const isMinimal = variant === 'minimal-dark' || variant === 'minimal-light';

  // Render the appropriate icon
  const renderIcon = () => {
    if (icon) {
      return icon;
    }
    return <SiteThemeIcon theme={theme} small={small} />;
  };

  // For pill variants
  if (isPill && config) {
    const displaySubtitle = config.productName ? config.productName : subtitle;

    return (
      <div
        className={classNames(
          styles.themedHeader,
          {
            [styles.pillOverlap]: variant === 'pill-overlap',
            [styles.pillSeparate]: variant === 'pill-separate',
            [styles.small]: small,
          },
          className
        )}
        style={
          {
            '--themed-header-primary': config.colors.primary,
          } as React.CSSProperties
        }
      >
        {variant === 'pill-separate' && (
          <div className={styles.pillIconWrapper}>{renderIcon()}</div>
        )}
        <div className={styles.pillContainer}>
          {variant === 'pill-overlap' && (
            <div className={styles.pillIconWrapper}>{renderIcon()}</div>
          )}
          <div className={styles.headerText}>
            <h3>{title}</h3>
            {displaySubtitle && (
              <div className={styles.subtitle}>{displaySubtitle}</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // For minimal variants
  if (isMinimal && config) {
    return (
      <div
        className={classNames(
          styles.themedHeader,
          styles.minimal,
          {
            [styles.minimalDark]: variant === 'minimal-dark',
            [styles.minimalLight]: variant === 'minimal-light',
            [styles.small]: small,
          },
          className
        )}
        style={
          {
            '--themed-header-primary': config.colors.primary,
          } as React.CSSProperties
        }
      >
        <div
          className={styles.minimalIcon}
          style={
            {
              '--themed-header-icon-path': `url(${config.iconPath})`,
            } as React.CSSProperties
          }
          role="img"
          aria-label={config.name}
        />
        <div className={styles.headerText}>
          <h3>{title}</h3>
          {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
        </div>
      </div>
    );
  }

  // Return the provided fallback or default to simple text without theming
  return fallbackComponent ? (
    <>{fallbackComponent}</>
  ) : (
    <div
      className={classNames(
        styles.themedHeader,
        { [styles.small]: small },
        className
      )}
    >
      {icon && <div className={styles.defaultIconWrapper}>{icon}</div>}
      <div className={styles.headerText}>
        <h3>{title}</h3>
        {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
      </div>
    </div>
  );
};

export const ContextThemedHeaderWithOptions: React.FC<ThemedHeaderOptions> = (
  props
) => {
  const context = useSiteThemeOptional();
  return context ? <ThemedHeader theme={context.theme} {...props} /> : null;
};
